import { selectSelectedApplicantsIds } from '../../../../../store/workspace/selectors';
import { getExaminerProfileThunk } from '../../../../../store/workspace/thunks';
import { CodingExamExaminerData } from '../../../../../store/workspace/types';
import { examStatusTitle } from '../../../../../utils/generalHelpers';
import { ForwardedRef, forwardRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from '../../../../../store/store';
import CheckBox from '../../../../../components/CheckBox';
import { useNavigate } from 'react-router-dom';
import styles from './style.module.scss';
import {
    setExaminerProfileModal,
    setSelectedApplicantsIds,
    setExaminerCodingExamData,
} from '../../../../../store/workspace/slice';
import {
    DangerIcon,
    ProfileIcon,
    DocumentIcon,
    ChevronUpIcon,
    TimesCircleIcon,
    ChevronDownIcon,
    CheckCircleIcon,
} from '../../../../../assets/icons';

interface Props {
    selectable: boolean;
    jobId: string | undefined;
    examinersInfo: CodingExamExaminerData[];
};

const CodingExamTable = forwardRef((props: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const {
        jobId,
        selectable,
        examinersInfo,
    } = props;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const selectedApplicantsIds = useSelector(selectSelectedApplicantsIds);
    const listCopy = examinersInfo ? [...examinersInfo] : [];
    const [tableInfo, setTableInfo] = useState(listCopy);
    const [expandedCards, setExpandedCards] = useState<string[]>([]);

    const toggleCard = (userId: string) => {
        setExpandedCards(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    useEffect(() => {
        sortTable('ascend');
    }, [examinersInfo]);

    const sortTable = (type: 'ascend' | 'descend') => {
        switch (type) {
            case 'ascend':
                listCopy.sort((a, b) => a.grade - b.grade);
                setTableInfo(listCopy);
                break;
            case 'descend':
                listCopy.sort((a, b) => b.grade - a.grade);
                setTableInfo(listCopy);
                break;
            default:
                return;
        }
    };

    const selectExaminer = (examinerId: string) => {
        const check = selectedApplicantsIds.some(i => i === examinerId);
        let list = [...selectedApplicantsIds];

        if (check) {
            list = list.filter(i => i !== examinerId);
        } else {
            list.push(examinerId);
        }

        dispatch(setSelectedApplicantsIds(list));
    };

    const selectAllExaminers = () => {
        const check = selectedApplicantsIds.length === examinersInfo.length;
        const newList = check ? [] : tableInfo.map(i => i.userId);
        dispatch(setSelectedApplicantsIds(newList));
    };

    const getExaminerProfile = (id: string) => {
        dispatch(getExaminerProfileThunk({ id, jobId }));
        dispatch(setExaminerProfileModal(true));
    };

    const goToExamDetail = (data: CodingExamExaminerData) => {
        dispatch(setExaminerCodingExamData(data));
        navigate('/workspace/history/coding_exam');
    };

    return (
        <div className={styles.table} ref={ref}>
            <table>
                <thead>
                    <tr>
                        <th colSpan={3} data-no-border="true">
                            {selectable && <div className={styles.select_all}>
                                <CheckBox
                                    onClick={selectAllExaminers}
                                    checked={tableInfo.length !== 0 && tableInfo.length === selectedApplicantsIds.length}
                                />
                                <span>Select All ({tableInfo.length})</span>
                            </div>}
                        </th>
                        <th data-no-border="true"></th>
                        <th colSpan={2} data-group-start="test-cases">
                            <span className={styles.head_section}>
                                Test Cases
                            </span>
                        </th>
                        <th colSpan={2} data-group-middle="complexity">
                            <span className={styles.head_section}>
                                Complexity
                            </span>
                        </th>
                        <th colSpan={5} data-group-end="code-quality">
                            <span className={styles.head_section}>
                                Code Quality
                            </span>
                        </th>
                    </tr>
                    <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>
                            <div className={styles.head_group}>
                                <div className={styles.head_group_sort}>
                                    <ChevronUpIcon onClick={() => sortTable('descend')} />
                                    <ChevronDownIcon onClick={() => sortTable('ascend')} />
                                </div>
                                <span>Grade</span>
                            </div>
                        </th>
                        <th>Q</th>
                        <th>Coverage</th>
                        <th>Time</th>
                        <th>Space</th>
                        <th>Time</th>
                        <th>Readability</th>
                        <th>Duplication</th>
                        <th>Efficiency</th>
                        <th>Complexity</th>
                        <th>Maintainability</th>
                        <th>Verification<br />constraint</th>
                        <th data-noprint="true">Sheet &<br />feedback</th>
                        <th data-noprint="true">Info</th>
                    </tr>
                </thead>
                <tbody>
                    {tableInfo.length === 0 ? (<tr><td colSpan={13}>No data.</td></tr>) :
                        tableInfo.map((q, indx) => <>
                            <tr key={q.userId} style={{ backgroundColor: indx % 2 === 1 ? '#F1F5F9' : 'transparent' }}>
                                <td rowSpan={3}>
                                    <div className={styles.examinerName}>
                                        {selectable && <CheckBox
                                            onClick={() => selectExaminer(q.userId)}
                                            checked={selectedApplicantsIds.some(i => i === q.userId)}
                                        />}
                                        <span>{q.first_name} {q.family_name}</span>
                                        {q.proctoring && <div className={styles.proctoring}>
                                            <DangerIcon />
                                            <span className={styles.proctoring_tooltip}>
                                                {q.first_name} {q.family_name} was caught cheating in this exam!
                                            </span>
                                        </div>}
                                    </div>
                                </td>
                                <td rowSpan={3}>
                                    <span
                                        className={styles.status}
                                        data-status={q.status}
                                    >
                                        {examStatusTitle(q.status)}
                                    </span>
                                </td>
                                <td rowSpan={3}>
                                    <div className={styles.grade}>
                                        <span className={styles.language}>{q.language}</span>
                                        <span>{q.grade}/10</span>
                                    </div>
                                </td>
                                <td>Q1</td>
                                <td>{q.scores[0]?.test_coverage}/10</td>
                                <td>{q.scores[0]?.execution_time.score}/10</td>
                                <td>{q.scores[0]?.space_complexity.score}/10</td>
                                <td>{q.scores[0]?.time_complexity.score}/10</td>
                                <td>{q.scores[0]?.readability}/10</td>
                                <td>{q.scores[0]?.duplication}/10</td>
                                <td>{q.scores[0]?.efficiency}/10</td>
                                <td>{q.scores[0]?.complexity}/10</td>
                                <td>{q.scores[0]?.maintainability}/10</td>
                                <td>{q.scores[0]?.verifies_constraints ? <CheckCircleIcon /> : <TimesCircleIcon />}</td>
                                <td></td>
                                <td></td>
                            </tr>
                            <tr style={{ backgroundColor: indx % 2 === 1 ? '#F1F5F9' : 'transparent' }}>
                                <td>Q2</td>
                                <td>{q.scores[1]?.test_coverage}/10</td>
                                <td>{q.scores[1]?.execution_time.score}/10</td>
                                <td>{q.scores[1]?.space_complexity.score}/10</td>
                                <td>{q.scores[1]?.time_complexity.score}/10</td>
                                <td>{q.scores[1]?.readability}/10</td>
                                <td>{q.scores[1]?.duplication}/10</td>
                                <td>{q.scores[1]?.efficiency}/10</td>
                                <td>{q.scores[1]?.complexity}/10</td>
                                <td>{q.scores[1]?.maintainability}/10</td>
                                <td>{q.scores[1]?.verifies_constraints ? <CheckCircleIcon /> : <TimesCircleIcon />}</td>
                                <td data-noprint="true"><DocumentIcon onClick={() => navigate(`${q.userId}`)} /></td>
                                <td data-noprint="true"><ProfileIcon onClick={() => getExaminerProfile(q.userId)} /></td>
                            </tr>
                            <tr style={{ backgroundColor: indx % 2 === 1 ? '#F1F5F9' : 'transparent' }}>
                                <td>Q3</td>
                                <td>{q.scores[2]?.test_coverage}/10</td>
                                <td>{q.scores[2]?.execution_time.score}/10</td>
                                <td>{q.scores[2]?.space_complexity.score}/10</td>
                                <td>{q.scores[2]?.time_complexity.score}/10</td>
                                <td>{q.scores[2]?.readability}/10</td>
                                <td>{q.scores[2]?.duplication}/10</td>
                                <td>{q.scores[2]?.efficiency}/10</td>
                                <td>{q.scores[2]?.complexity}/10</td>
                                <td>{q.scores[2]?.maintainability}/10</td>
                                <td>{q.scores[2]?.verifies_constraints ? <CheckCircleIcon /> : <TimesCircleIcon />}</td>
                                <td></td>
                                <td></td>
                            </tr>
                        </>)}
                </tbody>
            </table>

            <div className={styles.mobile_list}>
                {tableInfo.map(info => {
                    const isExpanded = expandedCards.includes(info.userId);
                    return (
                        <div className={styles.card}>
                            <div className={styles.card_header} onClick={() => toggleCard(info.userId)}>
                                <span>{info.first_name} {info.family_name}</span>
                                <ChevronDownIcon style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
                            </div>
                            {isExpanded && <div className={styles.card_body}>
                                <div className={styles.card_body}>
                                    <div className={styles.card_body_section}>
                                        <div className={styles.card_body_section_title}>
                                            <span>Status</span>
                                            <span className={styles.status} data-status={info.status}>
                                                {examStatusTitle(info.status)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.card_body_section}>
                                        <div className={styles.card_body_section_title}>
                                            <span>Grade</span>
                                            <span>{info.grade}/10</span>
                                        </div>
                                    </div>
                                    <div className={styles.card_body_section}>
                                        <div className={styles.card_body_section_title}>
                                            <span>Test Cases</span>
                                        </div>
                                        <div className={styles.card_body_section_content}>
                                            <div className={styles.card_body_section_content_group}>
                                                <div className={styles.card_body_section_content_group_title}>
                                                    <span>Q1</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details}>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Coverage</span>
                                                        <span>{info.scores[0]?.test_coverage}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Time</span>
                                                        <span>{info.scores[0]?.execution_time.score}/10</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={styles.card_body_section_content_group}>
                                                <div className={styles.card_body_section_content_group_title}>
                                                    <span>Q2</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details}>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Coverage</span>
                                                        <span>{info.scores[1]?.test_coverage}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Time</span>
                                                        <span>{info.scores[1]?.execution_time.score}/10</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={styles.card_body_section_content_group}>
                                                <div className={styles.card_body_section_content_group_title}>
                                                    <span>Q3</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details}>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Coverage</span>
                                                        <span>{info.scores[2]?.test_coverage}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Time</span>
                                                        <span>{info.scores[2]?.execution_time.score}/10</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.card_body_section}>
                                        <div className={styles.card_body_section_title}>
                                            <span>Complexity</span>
                                        </div>
                                        <div className={styles.card_body_section_content}>
                                            <div className={styles.card_body_section_content_group}>
                                                <div className={styles.card_body_section_content_group_title}>
                                                    <span>Q1</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details}>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Space</span>
                                                        <span>{info.scores[0]?.space_complexity.score}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Time</span>
                                                        <span>{info.scores[0]?.time_complexity.score}/10</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={styles.card_body_section_content_group}>
                                                <div className={styles.card_body_section_content_group_title}>
                                                    <span>Q2</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details}>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Space</span>
                                                        <span>{info.scores[1]?.space_complexity.score}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Time</span>
                                                        <span>{info.scores[1]?.time_complexity.score}/10</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={styles.card_body_section_content_group}>
                                                <div className={styles.card_body_section_content_group_title}>
                                                    <span>Q3</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details}>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Space</span>
                                                        <span>{info.scores[2]?.space_complexity.score}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Time</span>
                                                        <span>{info.scores[2]?.time_complexity.score}/10</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.card_body_section}>
                                            <div className={styles.card_body_section_title}>
                                                <span>Code Quality</span>
                                            </div>
                                            <div className={styles.card_body_section_content}>
                                                <div className={styles.card_body_section_content_group}>
                                                    <div className={styles.card_body_section_content_group_title}>
                                                        <span>Q1</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details}>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Readability</span>
                                                            <span>{info.scores[0]?.readability}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Duplication</span>
                                                            <span>{info.scores[0]?.duplication}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Efficiency</span>
                                                            <span>{info.scores[0]?.efficiency}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Complexity</span>
                                                            <span>{info.scores[0]?.complexity}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Maintainability</span>
                                                            <span>{info.scores[0]?.maintainability}/10</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className={styles.card_body_section_content_group}>
                                                    <div className={styles.card_body_section_content_group_title}>
                                                        <span>Q2</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details}>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Readability</span>
                                                            <span>{info.scores[1]?.readability}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Duplication</span>
                                                            <span>{info.scores[1]?.duplication}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Efficiency</span>
                                                            <span>{info.scores[1]?.efficiency}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Complexity</span>
                                                            <span>{info.scores[1]?.complexity}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Maintainability</span>
                                                            <span>{info.scores[1]?.maintainability}/10</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className={styles.card_body_section_content_group}>
                                                    <div className={styles.card_body_section_content_group_title}>
                                                        <span>Q3</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details}>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Readability</span>
                                                            <span>{info.scores[2]?.readability}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Duplication</span>
                                                            <span>{info.scores[2]?.duplication}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Efficiency</span>
                                                            <span>{info.scores[2]?.efficiency}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Complexity</span>
                                                            <span>{info.scores[2]?.complexity}/10</span>
                                                        </div>
                                                        <div className={styles.card_body_section_content_group_details_item}>
                                                            <span>Maintainability</span>
                                                            <span>{info.scores[2]?.maintainability}/10</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>}
                        </div>
                    )
                })}
            </div>
        </div>
    )
});

export default CodingExamTable;